import React, { useEffect, useMemo, useState } from "react";
import { MapContainer, TileLayer, CircleMarker, Marker, Tooltip, useMap } from "react-leaflet";
import L from "leaflet";
import { api } from "@/lib/api";
import { ChevronRight, Globe2, Building2, ArrowLeft, Phone, MapPin } from "lucide-react";
import { getCitiesForCountry } from "@/lib/locations";
import { getSuppliersInCity, getCitiesWithCoords, getDistrictsForCity, getSuppliersInDistrict } from "@/lib/suppliers";
import { useWizard } from "@/lib/wizard";
import { useI18n, tCountry } from "@/lib/i18n";

const NEON_CYAN  = "#00E5FF";
const NEON_GREEN = "#39FF14";
const NEON_WHITE = "#F5F5F5";
const NEON_RED   = "#FF3B30";
const NEON_AMBER = "#FFCC00";
const NEON_AMBER_DIST = "#FF9500";  // distinct orange for district markers

// Red MAP-PIN icon (SVG divIcon) — used to mark the selected country at the country level.
// Solid red fill, dark-red border, white centre dot. Anchored at the tip of the pin.
const COUNTRY_PIN_ICON = L.divIcon({
  className: "country-pin-icon",
  html:
    '<svg xmlns="http://www.w3.org/2000/svg" width="34" height="34" viewBox="0 0 24 24" ' +
    'style="filter: drop-shadow(0 0 4px rgba(255,0,0,0.85));">' +
    '<path d="M21 10c0 7-9 13-9 13s-9-6-9-13a9 9 0 0 1 18 0z" ' +
    'fill="#FF0000" stroke="#FFFFFF" stroke-width="1.6"/>' +
    '<circle cx="12" cy="10" r="3" fill="#FFFFFF"/>' +
    '</svg>',
  iconSize: [34, 34],
  iconAnchor: [17, 34],
  popupAnchor: [0, -34],
});
const NEON_YEL   = "#FFEB3B";

function FlyController({ target }) {
  const map = useMap();
  useEffect(() => {
    if (!target) return;
    const { lat, lon, zoom } = target;
    if (typeof lat !== "number" || typeof lon !== "number") return;
    map.flyTo([lat, lon], zoom, { duration: 1.2 });
  }, [target, map]);
  return null;
}

function MapResizer() {
  const map = useMap();
  useEffect(() => {
    const t1 = setTimeout(() => map.invalidateSize(), 60);
    const t2 = setTimeout(() => map.invalidateSize(), 300);
    return () => { clearTimeout(t1); clearTimeout(t2); };
  }, [map]);
  return null;
}

function Crumb({ active, color, label, sub, onClick, isLast }) {
  return (
    <button
      onClick={onClick}
      disabled={!onClick}
      className="flex items-center gap-2 px-3 py-1.5 border-2 bg-black transition-all font-mono text-[11px] uppercase tracking-[0.18em] font-bold"
      style={{
        borderColor: active ? color : "#1F1F1F",
        color: active ? color : "#8B8B95",
        boxShadow: active ? `0 0 12px ${color}55` : "none",
        textShadow: active ? `0 0 8px ${color}88` : "none",
        cursor: onClick ? "pointer" : "default",
        opacity: !onClick && !active ? 0.5 : 1,
      }}>
      <span>{label}</span>
      {sub && <span style={{ color: active ? color : "#FFFFFF" }}>· {sub}</span>}
      {!isLast && <ChevronRight className="w-3.5 h-3.5 ml-1"/>}
    </button>
  );
}

export default function InteractiveSuppliersMap() {
  const { setCountry: setWizardCountry, setCity: setWizardCity, setDistrict: setWizardDistrict } = useWizard();
  const { t, lang } = useI18n();
  const [countries, setCountries] = useState([]);
  const [level, setLevel] = useState("country");
  const [country, setCountry] = useState(null);
  const [city, setCity] = useState(null);
  const [district, setDistrict] = useState(null);
  const [flyTarget, setFlyTarget] = useState(null);
  const [activeSupplierIdx, setActiveSupplierIdx] = useState(null);
  const [isDay, setIsDay] = useState(() =>
    typeof document !== "undefined" && document.documentElement.classList.contains("light")
  );

  // Watch the <html> class so the tile layer flips when the user toggles Day/Night.
  useEffect(() => {
    if (typeof document === "undefined") return;
    const obs = new MutationObserver(() => {
      setIsDay(document.documentElement.classList.contains("light"));
    });
    obs.observe(document.documentElement, { attributes: true, attributeFilter: ["class"] });
    return () => obs.disconnect();
  }, []);

  useEffect(() => {
    api.get("/countries").then(({ data }) => setCountries(data)).catch(() => {});
  }, []);

  const cityList = useMemo(() => {
    if (!country) return [];
    return getCitiesWithCoords(country.code, getCitiesForCountry(country));
  }, [country]);

  const districtList = useMemo(() => {
    if (!country || !city) return [];
    return getDistrictsForCity(country.code, city.name);
  }, [country, city]);

  const suppliers = useMemo(() => {
    if (!country || !city) return [];
    if (district) return getSuppliersInDistrict(country.code, city.name, district.name);
    return getSuppliersInCity(country.code, city.name);
  }, [country, city, district]);

  const handleCountryClick = (c) => {
    // STATE 2: Country selected via dropdown → ONE red marker on the country.
    // Green city markers are NOT shown yet — user must click the red marker to advance.
    setCountry(c); setCity(null); setDistrict(null);
    setLevel("country-selected");
    setActiveSupplierIdx(null);
    setFlyTarget({ lat: c.lat, lon: c.lon, zoom: 5 });
    setWizardCountry && setWizardCountry(c);
  };
  const handleCountryPinClick = () => {
    // STATE 3: User clicked the red country pin → red disappears, green cities appear.
    setLevel("city");
    if (country) setFlyTarget({ lat: country.lat, lon: country.lon, zoom: 6 });
  };
  const handleCityClick = (cityObj) => {
    // STATE 4: User clicked a green city marker → sidebar populates with BLUE supplier info.
    setCity(cityObj); setDistrict(null); setLevel("supplier");
    setActiveSupplierIdx(null);
    setFlyTarget({ lat: cityObj.lat, lon: cityObj.lon, zoom: 11 });
    setWizardCity && setWizardCity(cityObj.name);
  };
  const handleDistrictClick = (d) => {
    setDistrict(d);
    setActiveSupplierIdx(null);
    setFlyTarget({ lat: d.lat, lon: d.lon, zoom: 13 });
    setWizardDistrict && setWizardDistrict(d.name);
  };
  const goWorld = () => {
    setCountry(null); setCity(null); setDistrict(null); setLevel("country");
    setActiveSupplierIdx(null);
    setFlyTarget({ lat: 20, lon: 0, zoom: 2 });
  };
  const goCountry = () => {
    if (!country) return;
    setCity(null); setDistrict(null); setLevel("city");
    setActiveSupplierIdx(null);
    setFlyTarget({ lat: country.lat, lon: country.lon, zoom: 6 });
  };
  const goCity = () => {
    if (!city) return;
    setDistrict(null);
    setActiveSupplierIdx(null);
    setFlyTarget({ lat: city.lat, lon: city.lon, zoom: 11 });
  };
  const focusSupplier = (idx) => {
    setActiveSupplierIdx(idx);
    const s = suppliers[idx];
    if (s) setFlyTarget({ lat: s.lat, lon: s.lon, zoom: 14 });
  };

  // Marker visibility per the strict 4-state spec.
  const showRedCountryPin = level === "country-selected" && country;
  const showCities        = (level === "city" || level === "supplier") && country;
  const showDistricts     = level === "supplier" && city;
  const showSuppliers     = level === "supplier" && city;

  return (
    <section
      className="border-2 bg-black"
      style={{ borderColor: NEON_CYAN, boxShadow: `0 0 18px ${NEON_CYAN}33, inset 0 0 12px ${NEON_CYAN}11` }}
      data-testid="interactive-suppliers-map">

      {/* Header bar */}
      <header className="bg-[#0A0A0A] border-b-2 border-[#1F1F1F] px-4 py-3 flex items-center gap-3 flex-wrap">
        <Globe2 className="w-5 h-5" style={{ color: NEON_CYAN }}/>
        <span
          data-testid="suppliers-map-title"
          className="font-mono text-sm uppercase tracking-[0.22em]"
          style={{
            color: '#FFFFFF',
            WebkitTextFillColor: '#FFFFFF',
            fontWeight: 800,
            opacity: 1,
            textShadow: '0 1px 4px rgba(0,0,0,0.6)'
          }}>
          {t("map.title", "Suppliers · Interactive Drill-Down")}
        </span>
        <div className="flex items-center gap-2 ml-auto flex-wrap">
          <Crumb active={level === "country"} color={NEON_CYAN} label={t("map.world", "World")}
            sub={tCountry(country, lang)} onClick={level !== "country" ? goWorld : null} isLast={!country}/>
          {country && (
            <Crumb active={level === "city"} color={NEON_AMBER} label={t("map.city", "City")}
              sub={city?.name} onClick={level === "supplier" ? goCountry : null} isLast={!city}/>
          )}
          {city && (
            <Crumb active={level === "supplier" && !district} color={NEON_GREEN}
              label={t("map.suppliers", "Suppliers")}
              sub={`${suppliers.length} ${t("map.firms", "firms")}`}
              onClick={district ? goCity : null}
              isLast={!district}/>
          )}
          {district && (
            <Crumb active={true} color={NEON_AMBER_DIST}
              label={t("map.district", "District")}
              sub={district.name}
              onClick={null} isLast={true}/>
          )}
        </div>
      </header>

      {/* Country dropdown selector */}
      <div className="px-4 py-3 bg-[#050505] border-b-2 border-[#1F1F1F] flex items-center gap-3 flex-wrap"
        data-testid="country-dropdown-bar">
        <Globe2 className="w-4 h-4" style={{ color: NEON_CYAN }}/>
        <label className="font-mono text-[11px] uppercase tracking-[0.22em] font-extrabold"
          style={{ color: NEON_CYAN, textShadow: `0 0 6px ${NEON_CYAN}66` }}>
          {t("map.pickCountry", "Pick a country")}:
        </label>
        <select
          data-testid="country-select"
          value={country?.code || ""}
          onChange={(e) => {
            const c = countries.find((cc) => cc.code === e.target.value);
            if (c) handleCountryClick(c);
            else goWorld();
          }}
          className="bg-black border-2 border-[#00E5FF] text-[#00E5FF] font-mono font-bold text-[12px] uppercase tracking-wider px-3 py-1.5 hover:bg-[#00E5FF]/10 focus:outline-none"
          style={{ boxShadow: `0 0 6px ${NEON_CYAN}33` }}>
          <option value="" style={{ background: "#000000", color: "#00E5FF" }}>— {t("map.selectCountry", "Select a country")} —</option>
          {countries
            .slice()
            .sort((a, b) => tCountry(a, lang).localeCompare(tCountry(b, lang)))
            .map((c) => (
              <option key={c.code} value={c.code} style={{ background: "#000000", color: "#FFFFFF" }}>
                {tCountry(c, lang)}
              </option>
            ))}
        </select>
        {country && (
          <button
            type="button"
            onClick={goWorld}
            data-testid="country-clear"
            className="px-2 py-1 border-2 border-[#FF3B30] text-[#FF3B30] font-mono text-[10px] uppercase tracking-wider font-bold hover:bg-[#FF3B30]/10">
            ✕ {t("common.clear", "Clear")}
          </button>
        )}
      </div>

      {/* Status bar */}
      <div className="px-4 py-2 bg-[#050505] border-b border-[#1F1F1F] font-mono text-[11px] font-bold flex items-center gap-3 flex-wrap"
        data-testid="suppliers-status-bar">
        {level === "country" && (
          <span className="text-white/80">
            <span style={{ color: NEON_CYAN }}>{t("map.step1", "STEP 1 →")}</span> {t("map.step1Hint", "Click a country marker to reveal its cities.")}
          </span>
        )}
        {level === "country-selected" && country && (
          <>
            <button onClick={goWorld}
              className="px-2 py-1 border-2 border-[#1F1F1F] hover:border-[#00E5FF] flex items-center gap-1 text-white"
              data-testid="back-to-world">
              <ArrowLeft className="w-3 h-3"/> {t("map.world", "World")}
            </button>
            <span className="text-white/80">
              <span style={{ color: "#FF3B30" }}>{t("map.step1b", "STEP 1B →")}</span>{" "}
              {t("map.clickRedPin", "Click the red pin on")}{" "}
              <span style={{ color: "#FF3B30", textShadow: "0 0 6px #FF3B30" }}>{country.name}</span>{" "}
              {t("map.toShowCities", "to reveal its cities (green markers).")}
            </span>
          </>
        )}
        {level === "city" && country && (
          <>
            <button onClick={goWorld}
              className="px-2 py-1 border-2 border-[#1F1F1F] hover:border-[#00E5FF] flex items-center gap-1 text-white"
              data-testid="back-to-world">
              <ArrowLeft className="w-3 h-3"/> {t("map.world", "World")}
            </button>
            <span className="text-white/80">
              <span style={{ color: NEON_AMBER }}>{t("map.step2", "STEP 2 →")}</span> {t("map.step2Hint", "Click a city marker. Selected city turns red.")}
            </span>
          </>
        )}
        {level === "supplier" && city && (
          <>
            <button onClick={goCountry}
              className="px-2 py-1 border-2 border-[#1F1F1F] hover:border-[#FFCC00] flex items-center gap-1 text-white"
              data-testid="back-to-country">
              <ArrowLeft className="w-3 h-3"/> {country?.name}
            </button>
            <span className="text-white/80">
              <span style={{ color: NEON_GREEN }}>{t("map.step3", "STEP 3 →")}</span> {t("map.step3Hint", "Green markers show suppliers in")}
              <span style={{ color: NEON_RED, marginLeft: 6 }}>{city.name}</span>.
              {districtList.length > 0 && (
                <span className="ml-2" style={{ color: NEON_AMBER_DIST }}>
                  · {t("map.districtsHint", "Click an orange district marker to filter.")}
                </span>
              )}
            </span>
          </>
        )}
      </div>

      {/* MAP + SIDEBAR side-by-side */}
      <div className="grid grid-cols-1 lg:grid-cols-12">
        {/* MAP */}
        <div className="lg:col-span-8" style={{ height: 620, position: "relative" }}>
          <MapContainer
            center={[20, 0]} zoom={2} scrollWheelZoom={true} worldCopyJump={true}
            style={{ height: "100%", width: "100%", background: "#000" }}
            data-testid="suppliers-map">
            <MapResizer/>
            <FlyController target={flyTarget}/>
            <TileLayer
              attribution='&copy; <a href="https://carto.com/attributions">CARTO</a>'
              key={isDay ? "light" : "dark"}
              url={isDay
                ? "https://{s}.basemaps.cartocdn.com/light_all/{z}/{x}/{y}{r}.png"
                : "https://{s}.basemaps.cartocdn.com/dark_all/{z}/{x}/{y}{r}.png"}
              subdomains="abcd" maxZoom={19}
            />

            {/* STATE 2: ONE solid RED PIN-shaped marker for the selected country.
                Click to advance to cities. Uses a Leaflet divIcon SVG pin (not a circle). */}
            {showRedCountryPin && (
              <Marker key={country.code} position={[country.lat, country.lon]}
                icon={COUNTRY_PIN_ICON}
                eventHandlers={{
                  click: () => { console.log("[Map] red country pin clicked"); handleCountryPinClick(); },
                }}>
                <Tooltip direction="top" offset={[0, -32]} opacity={1} permanent className="country-label-tooltip">
                  <span style={{
                    color: "#2196F3",
                    fontFamily: "Inter, Roboto, Arial, sans-serif",
                    fontWeight: 800,
                    fontSize: "14px",
                    letterSpacing: "0.3px",
                    textShadow: "0 0 3px rgba(0,0,0,0.85), 0 0 6px rgba(0,0,0,0.7)",
                  }}>
                    {tCountry(country, lang)} <span style={{ color: "#FF3B30", fontSize: "11px" }}>▼</span>
                  </span>
                </Tooltip>
              </Marker>
            )}

            {/* LEVEL 2: Cities (NEON GREEN with always-on tooltip) */}
            {showCities && cityList.map((cityObj) => {
              const isSel = city && city.name === cityObj.name;
              const fill = isSel ? NEON_YEL : "#00FF00";
              const ring = isSel ? NEON_YEL : "#00FF00";
              return (
                <CircleMarker key={`city-${cityObj.name}`} center={[cityObj.lat, cityObj.lon]}
                  radius={isSel ? 11 : 8}
                  pathOptions={{
                    color: ring, weight: isSel ? 3 : 2,
                    fillColor: fill, fillOpacity: isSel ? 1 : 0.85,
                    className: "neon-green-glow",
                  }}
                  eventHandlers={{
                    click: () => { console.log("[Map] city clicked", cityObj.name); handleCityClick(cityObj); },
                    mouseover: (ev) => ev.target.setStyle({ radius: 13, fillOpacity: 1 }),
                    mouseout:  (ev) => ev.target.setStyle({
                      radius: isSel ? 11 : 8, fillOpacity: isSel ? 1 : 0.85,
                    }),
                  }}>
                  <Tooltip direction="top" offset={[0, -10]} opacity={1} permanent className="city-label-tooltip">
                    <span style={{
                      color: isSel ? NEON_YEL : "#2196F3",
                      fontFamily: "Inter, Roboto, Arial, sans-serif",
                      fontWeight: 700,
                      fontSize: "12px",
                      letterSpacing: "0.3px",
                      textShadow: "0 0 3px rgba(0,0,0,0.85), 0 0 6px rgba(0,0,0,0.7)",
                    }}>
                      {cityObj.name}
                    </span>
                  </Tooltip>
                </CircleMarker>
              );
            })}

            {/* LEVEL 2.5: District markers (NEON WHITE) — visible when a city is selected */}
            {showDistricts && districtList.map((d) => {
              const isSel = district && district.name === d.name;
              const fill = isSel ? NEON_YEL : "#FFFFFF";
              const ring = isSel ? NEON_YEL : "#FFFFFF";
              return (
                <CircleMarker key={`dist-${d.name}`} center={[d.lat, d.lon]}
                  radius={isSel ? 8 : 5}
                  pathOptions={{
                    color: ring, weight: isSel ? 3 : 2,
                    fillColor: fill,
                    fillOpacity: isSel ? 1 : 0.9,
                    className: "neon-white-glow",
                  }}
                  eventHandlers={{
                    click: () => { console.log("[Map] district clicked", d.name); handleDistrictClick(d); },
                    mouseover: (ev) => ev.target.setStyle({ radius: 9, fillOpacity: 1 }),
                    mouseout:  (ev) => ev.target.setStyle({
                      radius: isSel ? 8 : 5,
                      fillOpacity: isSel ? 1 : 0.9,
                    }),
                  }}>
                  <Tooltip direction="top" offset={[0, -6]} opacity={0.95}
                    permanent={isSel}>
                    <span style={{
                      color: isSel ? NEON_YEL : "#FFFFFF",
                      fontFamily: "monospace", fontWeight: 800, fontSize: 11,
                      textShadow: `0 0 4px ${isSel ? NEON_YEL : "#FFFFFF"}`,
                    }}>
                      {d.name}
                    </span>
                  </Tooltip>
                </CircleMarker>
              );
            })}

            {/* LEVEL 3: Suppliers */}
            {showSuppliers && suppliers.map((s, i) => {
              const active = activeSupplierIdx === i;
              return (
                <CircleMarker key={`sup-${i}`} center={[s.lat, s.lon]}
                  radius={active ? 14 : 9}
                  pathOptions={{
                    color: NEON_GREEN, weight: active ? 4 : 3,
                    fillColor: NEON_GREEN, fillOpacity: 0.95,
                  }}
                  eventHandlers={{
                    click: () => setActiveSupplierIdx(i),
                  }}>
                  <Tooltip direction="top" offset={[0, -10]} opacity={1} permanent>
                    <div style={{
                      background: "#000", border: `2px solid ${active ? NEON_YEL : NEON_GREEN}`,
                      padding: "6px 8px", fontFamily: "monospace",
                      boxShadow: `0 0 ${active ? 14 : 10}px ${active ? NEON_YEL : NEON_GREEN}88`,
                    }}>
                      <div style={{ color: NEON_YEL, fontWeight: 900, fontSize: 12, letterSpacing: 0.5 }}>
                        {s.name}
                      </div>
                      <div style={{ color: NEON_GREEN, fontWeight: 700, fontSize: 11, marginTop: 2 }}>
                        ☎ {s.phone}
                      </div>
                    </div>
                  </Tooltip>
                </CircleMarker>
              );
            })}
          </MapContainer>
        </div>

        {/* SIDEBAR */}
        <aside
          className="lg:col-span-4 flex flex-col"
          style={{
            background: "#0A0A0A",
            borderLeft: `2px solid ${NEON_CYAN}55`,
            boxShadow: `inset 6px 0 16px ${NEON_CYAN}11`,
            minHeight: 620,
          }}
          data-testid="suppliers-sidebar">
          <div className="px-4 py-3 border-b-2 border-[#1F1F1F] flex items-center gap-2"
            style={{ background: "#050505" }}>
            <Building2 className="w-4 h-4" style={{ color: NEON_GREEN }}/>
            <span className="font-mono text-[12px] uppercase tracking-[0.22em] font-extrabold"
              style={{ color: NEON_GREEN }}>
              {district
                ? `${city.name} › ${district.name}`
                : city
                  ? `${city.name} · ${t("map.suppliers", "Suppliers")}`
                  : t("map.suppliers", "Suppliers")}
            </span>
            {city && (
              <span className="ml-auto font-mono text-[10px] uppercase font-bold text-white/60">
                {suppliers.length} {t("map.firms", suppliers.length === 1 ? "firm" : "firms")}
              </span>
            )}
            {district && (
              <button onClick={goCity}
                className="ml-2 px-2 py-0.5 border border-[#1F1F1F] hover:border-[#39FF14] font-mono text-[10px] uppercase tracking-wider font-bold text-white"
                data-testid="clear-district-filter">
                ✕ {t("map.allDistricts", "All districts")}
              </button>
            )}
          </div>

          <div className="flex-1 overflow-y-auto p-3" data-testid="suppliers-sidebar-list">
            {/* Selected location pill */}
            {(country || city || district) && (
              <div className="font-mono text-[10px] font-bold mb-2 px-2 py-1 border border-[#1F1F1F] bg-black flex items-center gap-1.5"
                data-testid="sidebar-location-pill">
                <MapPin className="w-3 h-3" style={{ color: NEON_GREEN }}/>
                <span style={{ color: NEON_CYAN }}>{country?.code || "—"}</span>
                {city && <><span className="text-white/40">›</span><span style={{ color: "#FFFFFF" }}>{city.name}</span></>}
                {district && <><span className="text-white/40">›</span><span style={{ color: NEON_YEL }}>{district.name}</span></>}
                <span className="ml-auto" style={{ color: NEON_GREEN }}>{suppliers.length} {t("map.firms", "firms")}</span>
              </div>
            )}
            {!country && (
              <SidebarPlaceholder
                icon={Globe2} color={NEON_CYAN}
                title={t("map.selectCountry", "Select a country")}
                hint={t("map.selectCountryHint", "Pick a country on the map to begin.")}/>
            )}
            {country && !city && (
              <SidebarPlaceholder
                icon={MapPin} color={NEON_RED}
                title={t("map.selectCity", "Select a city")}
                hint={t("map.selectCityHint", "Click a red city marker on the map. Its suppliers will appear here.")}/>
            )}
            {country && city && suppliers.length === 0 && (
              <SidebarPlaceholder
                icon={Building2} color={NEON_RED}
                title={t("map.noSuppliers", "No suppliers yet")}
                hint={t("map.noSuppliersHint", "No registered suppliers for this city.")}/>
            )}
            {country && city && suppliers.length > 0 && (
              <ul className="space-y-2">
                {suppliers.map((s, i) => {
                  const active = activeSupplierIdx === i;
                  return (
                    <li key={i}>
                      <button
                        onClick={() => focusSupplier(i)}
                        data-testid={`supplier-card-${i}`}
                        className="w-full text-left px-3 py-3 border-2 bg-black transition-all duration-200 group"
                        style={{
                          borderColor: active ? NEON_YEL : NEON_GREEN + "55",
                          boxShadow: active
                            ? `0 0 16px ${NEON_YEL}66, inset 0 0 8px ${NEON_YEL}22`
                            : `0 0 6px ${NEON_GREEN}22`,
                        }}
                        onMouseEnter={(e) => {
                          if (!active) {
                            e.currentTarget.style.borderColor = NEON_GREEN;
                            e.currentTarget.style.boxShadow = `0 0 12px ${NEON_GREEN}55`;
                          }
                        }}
                        onMouseLeave={(e) => {
                          if (!active) {
                            e.currentTarget.style.borderColor = NEON_GREEN + "55";
                            e.currentTarget.style.boxShadow = `0 0 6px ${NEON_GREEN}22`;
                          }
                        }}>
                        <div className="flex items-start gap-2">
                          <span className="w-2 h-2 mt-1.5 rounded-full"
                            style={{
                              background: active ? NEON_YEL : "#00BFFF",
                              boxShadow: `0 0 8px ${active ? NEON_YEL : "#00BFFF"}`,
                            }}/>
                          <div className="flex-1 min-w-0">
                            <div className="font-mono text-[13px] font-extrabold truncate"
                              style={{
                                color: "#60A5FA",
                                textShadow: active ? `0 0 8px #60A5FA88` : "0 0 4px #60A5FA55",
                              }}>
                              {s.name}
                            </div>
                            <a href={`tel:${s.phone.replace(/\s+/g, "")}`}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-1.5 mt-1 font-mono text-[12px] font-bold hover:underline"
                              style={{ color: "#3B82F6" }}
                              data-testid={`supplier-phone-${i}`}>
                              <Phone className="w-3 h-3"/> {s.phone}
                            </a>
                            {s.district && (
                              <div className="font-mono text-[10px] font-bold mt-1"
                                style={{ color: NEON_AMBER_DIST, textShadow: `0 0 4px ${NEON_AMBER_DIST}55` }}>
                                ◆ {s.district}
                              </div>
                            )}
                          </div>
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </div>
        </aside>
      </div>
    </section>
  );
}

function SidebarPlaceholder({ icon: Icon, color, title, hint }) {
  return (
    <div className="flex flex-col items-center justify-center text-center p-6 mt-10">
      <Icon className="w-10 h-10 mb-3" style={{ color, filter: `drop-shadow(0 0 8px ${color})` }}/>
      <div className="font-mono text-[13px] font-extrabold uppercase tracking-[0.22em] mb-1"
        style={{ color, textShadow: `0 0 8px ${color}55` }}>
        {title}
      </div>
      <div className="font-mono text-[11px] font-bold text-white/60 leading-snug max-w-[260px]">
        {hint}
      </div>
    </div>
  );
}
